import { useEffect, useRef } from 'react';
import { useLocation } from '@tanstack/react-router';

export type PageViewTrackingOptions = {
  /**
   * Key the page view on the pathname alone, ignoring the query string. Use this on pages that
   * keep UI state (sort, filters, pagination) in the URL, where a query change is the same view
   * rather than a new one. Defaults to `false` so a query change counts as a new page view.
   */
  ignoreSearch?: boolean;
};

/**
 * A hook that tracks page views based on location changes.
 * The tracking callback will fire once when the component mounts and whenever the location changes.
 *
 * @param trackingCallback - Function to call when a page view should be tracked
 * @param enabled - Whether tracking is enabled (default: true)
 * @param options - See {@link PageViewTrackingOptions}
 *
 * @example
 * ```tsx
 * usePageViewTracking(() => {
 *   analytics.track('myPage', 'viewed', {
 *     Source: 'navigation',
 *     'User ID': userId,
 *   });
 * });
 * ```
 */
export function usePageViewTracking(
  trackingCallback: () => void,
  enabled = true,
  { ignoreSearch = false }: PageViewTrackingOptions = {},
) {
  const locationKey = useLocation({
    select: (location) => (ignoreSearch ? location.pathname : `${location.pathname}${location.searchStr}`),
  });
  const trackedLocationRef = useRef<string | null>(null);
  const callbackRef = useRef(trackingCallback);
  callbackRef.current = trackingCallback;

  useEffect(() => {
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- existing effect pattern before this rule was enforced strictly; avoid adding new cases
    if (!enabled) {
      return;
    }

    // Only track if we haven't tracked this exact location yet
    if (trackedLocationRef.current !== locationKey) {
      callbackRef.current();
      trackedLocationRef.current = locationKey;
    }
  }, [locationKey, enabled]);
}
